import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGestaoUsuarios } from '../context/GestaoUsuariosProvider';
import { useUserManagementPermissions } from '../hooks/useUserManagementPermissions';
import { ArrowLeft, Users, Save, Eye, EyeOff, Shield, Settings, EyeIcon } from 'lucide-react';
import toast from 'react-hot-toast';

const CadastroUsuario = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  
  const { criarUsuario, atualizarUsuario, obterUsuario, loading } = useGestaoUsuarios();
  const { 
    canCreateUsers, 
    canEditUsers, 
    canCreateUserWithLevel,
    canEditUser,
    loading: permissionsLoading 
  } = useUserManagementPermissions();
  
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    nivel_acesso: 'visualizador',
    senha_temporaria: '',
    ativo: true,
    permissoes: {
      obras: false,
      'obras-fixas': false,
      'fossas-septicas': false,
      'postes-iluminacao': false,
      'controle-demandas': false,
      'mapa-integrado': false,
      'gestao-usuarios': false
    }
  });
  
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  // Carregar dados do usuário se estiver editando
  useEffect(() => {
    if (isEditing) {
      const loadUser = async () => {
        try {
          const usuario = await obterUsuario(id);
          setFormData({
            nome: usuario.nome || '',
            email: usuario.email || '',
            telefone: usuario.celular || '',
            nivel_acesso: usuario.nivel_acesso || 'visualizador',
            senha_temporaria: '',
            ativo: usuario.ativo,
            permissoes: usuario.permissoes || {
              obras: false,
              'obras-fixas': false,
              'fossas-septicas': false,
              'postes-iluminacao': false,
              'controle-demandas': false,
              'mapa-integrado': false,
              'gestao-usuarios': false
            }
          });
        } catch (error) {
          console.error('Erro ao carregar usuário:', error);
        }
      };
      loadUser();
    }
  }, [id, isEditing, obterUsuario]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Limpar erro do campo
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handlePermissaoChange = (modulo, checked) => {
    setFormData(prev => ({
      ...prev,
      permissoes: {
        ...prev.permissoes,
        [modulo]: checked
      }
    }));
  };

  const handleNivelAcessoChange = (nivel) => {
    let novasPermissoes = { ...formData.permissoes };
    
    // Definir permissões baseadas no nível
    switch (nivel) {
      case 'admin':
        // Admin tem acesso a tudo
        Object.keys(novasPermissoes).forEach(key => {
          novasPermissoes[key] = true;
        });
        break;
      case 'operador':
        // Operador não tem acesso à gestão de usuários por padrão
        novasPermissoes['gestao-usuarios'] = false;
        break;
      case 'visualizador':
        // Visualizador só tem acesso ao mapa integrado
        Object.keys(novasPermissoes).forEach(key => {
          novasPermissoes[key] = false;
        });
        novasPermissoes['mapa-integrado'] = true;
        break;
    }
    
    setFormData(prev => ({
      ...prev,
      nivel_acesso: nivel,
      permissoes: novasPermissoes
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    
    if (!isEditing && !formData.senha_temporaria) {
      newErrors.senha_temporaria = 'Senha é obrigatória';
    } else if (!isEditing && formData.senha_temporaria.length < 6) {
      newErrors.senha_temporaria = 'Senha deve ter pelo menos 6 caracteres';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    // Verificar permissões antes de submeter
    if (isEditing) {
      if (!canEditUsers) {
        toast.error('Você não tem permissão para editar usuários');
        return;
      }
      
      // Verificar se pode editar este usuário específico
      const usuario = await obterUsuario(id);
      if (!canEditUser(id, usuario?.nivel_acesso)) {
        toast.error('Você não tem permissão para editar este usuário');
        return;
      }
    } else {
      if (!canCreateUsers) {
        toast.error('Você não tem permissão para criar usuários');
        return;
      }
      
      // Verificar se pode criar usuário com este nível
      if (!canCreateUserWithLevel(formData.nivel_acesso)) {
        toast.error(`Você não tem permissão para criar usuários com nível ${formData.nivel_acesso}`);
        return;
      }
    }
    
    try {
      if (isEditing) {
        await atualizarUsuario(id, formData);
      } else {
        await criarUsuario(formData);
      }
      navigate('/gestao-usuarios');
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
    }
  };

  const modulosDisponiveis = [
    { key: 'obras', label: 'Obras em Vias Públicas', icon: '🚧' },
    { key: 'obras-fixas', label: 'Obras Fixas', icon: '🏗️' },
    { key: 'fossas-septicas', label: 'Fossas Sépticas', icon: '🏠' },
    { key: 'postes-iluminacao', label: 'Postes de Iluminação', icon: '💡' },
    { key: 'controle-demandas', label: 'Controle de Demandas', icon: '📋' },
    { key: 'mapa-integrado', label: 'Mapa Integrado', icon: '🗺️' },
    { key: 'gestao-usuarios', label: 'Gestão de Usuários', icon: '👥' }
  ];



  // Verificar permissões antes de renderizar
  if (permissionsLoading) {
    return (
      <div className="gestao-usuarios cadastro-full-container">
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <div style={{ marginBottom: '20px' }}>
            <div style={{ 
              border: '4px solid rgba(0, 0, 0, 0.1)',
              borderLeftColor: '#2e7d32',
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
          </div>
          <div>Verificando permissões...</div>
        </div>
      </div>
    );
  }

  // Verificar se tem permissão para acessar esta página
  if (isEditing && !canEditUsers) {
    return (
      <div className="gestao-usuarios cadastro-full-container">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <div style={{ textAlign: 'center', maxWidth: '400px' }}>
            <h3>Acesso Negado</h3>
            <p>Você não tem permissão para editar usuários.</p>
            <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
              Certifique-se de que você é administrador com permissão de gestão de usuários.
            </p>
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button onClick={() => navigate('/gestao-usuarios')} className="button primary">
                Voltar
              </button>
              <button onClick={() => window.location.reload()} className="button secondary">
                Recarregar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isEditing && !canCreateUsers) {
    return (
      <div className="gestao-usuarios cadastro-full-container">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <div style={{ textAlign: 'center', maxWidth: '400px' }}>
            <h3>Acesso Negado</h3>
            <p>Você não tem permissão para criar usuários.</p>
            <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
              Certifique-se de que você é administrador com permissão de gestão de usuários.
            </p>
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button onClick={() => navigate('/gestao-usuarios')} className="button primary">
                Voltar
              </button>
              <button onClick={() => window.location.reload()} className="button secondary">
                Recarregar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gestao-usuarios cadastro-full-container">
      <div className="cadastro-header">
        <button 
          className="back-button"
          onClick={() => navigate('/gestao-usuarios')}
        >
          <ArrowLeft size={20} />
        </button>
        <div className="header-content">
          <h1>
            <Users size={32} />
            {isEditing ? 'Editar Usuário' : 'Novo Usuário'}
          </h1>
          <p>{isEditing ? 'Edite as informações do usuário' : 'Cadastre um novo usuário no sistema'}</p>
        </div>
      </div>

      <div className="cadastro-content-wrapper">
        <form onSubmit={handleSubmit} className="cadastro-form">
          <div className="form-grid">
            {/* Informações Básicas */}
            <div className="form-section">
              <h3>Informações Básicas</h3>
              
              <div className="form-group">
                <label>Nome Completo *</label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleInputChange}
                  className={errors.nome ? 'error' : ''}
                  placeholder="Digite o nome completo"
                />
                {errors.nome && (
                  <div className="error-message">
                    {errors.nome}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={errors.email ? 'error' : ''}
                  placeholder="usuario@exemplo.com"
                  disabled={isEditing} // Email não pode ser alterado
                />
                {errors.email && (
                  <div className="error-message">
                    {errors.email}
                  </div>
                )}
                {isEditing && (
                  <small style={{ color: '#666', fontSize: '12px' }}>
                    O email não pode ser alterado após o cadastro
                  </small>
                )}
              </div>

              <div className="form-group">
                <label>Telefone</label>
                <input
                  type="tel"
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleInputChange}
                  placeholder="(27) 99999-9999"
                />
              </div>

              {!isEditing && (
                <div className="form-group">
                  <label>Senha *</label>
                  <div className="input-with-button">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="senha_temporaria"
                      value={formData.senha_temporaria}
                      onChange={handleInputChange}
                      className={errors.senha_temporaria ? 'error' : ''}
                      placeholder="Senha do usuário (mín. 6 caracteres)"
                    />
                    <button
                      type="button"
                      className="location-button"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {errors.senha_temporaria && (
                    <div className="error-message">
                      {errors.senha_temporaria}
                    </div>
                  )}
                  <small style={{ color: '#666', fontSize: '12px' }}>
                    Senha que será usada pelo usuário para acessar o sistema
                  </small>
                </div>
              )}
            </div>

            {/* Nível de Acesso */}
            <div className="form-section">
              <h3>Nível de Acesso</h3>
              
              <div className="access-levels">
                <div 
                  className={`access-level-card ${formData.nivel_acesso === 'admin' ? 'selected' : ''}`}
                  onClick={() => handleNivelAcessoChange('admin')}
                >
                  <div className="access-level-icon admin">
                    <Shield size={20} />
                  </div>
                  <div className="access-level-content">
                    <h4>Administrador</h4>
                    <p>Acesso total ao sistema</p>
                  </div>
                  <input
                    type="radio"
                    name="nivel_acesso"
                    value="admin"
                    checked={formData.nivel_acesso === 'admin'}
                    onChange={() => {}}
                  />
                </div>

                <div 
                  className={`access-level-card ${formData.nivel_acesso === 'operador' ? 'selected' : ''}`}
                  onClick={() => handleNivelAcessoChange('operador')}
                >
                  <div className="access-level-icon operador">
                    <Settings size={20} />
                  </div>
                  <div className="access-level-content">
                    <h4>Operador</h4>
                    <p>Acesso a módulos específicos</p>
                  </div>
                  <input
                    type="radio"
                    name="nivel_acesso"
                    value="operador"
                    checked={formData.nivel_acesso === 'operador'}
                    onChange={() => {}}
                  />
                </div>

                <div 
                  className={`access-level-card ${formData.nivel_acesso === 'visualizador' ? 'selected' : ''}`}
                  onClick={() => handleNivelAcessoChange('visualizador')}
                >
                  <div className="access-level-icon visualizador">
                    <EyeIcon size={20} />
                  </div>
                  <div className="access-level-content">
                    <h4>Visualizador</h4>
                    <p>Apenas visualização</p>
                  </div>
                  <input
                    type="radio"
                    name="nivel_acesso"
                    value="visualizador"
                    checked={formData.nivel_acesso === 'visualizador'}
                    onChange={() => {}}
                  />
                </div>
              </div>
            </div>

            {/* Permissões por Módulo */}
            <div className="form-section">
              <h3>Permissões por Módulo</h3>
              
              <div className="permissions-grid">
                {modulosDisponiveis.map(modulo => {
                  // Determinar se o módulo deve estar desabilitado
                  let isDisabled = false;
                  let disabledReason = '';
                  
                  if (formData.nivel_acesso === 'admin') {
                    isDisabled = true; // Admin sempre tem tudo
                    disabledReason = 'Administradores têm acesso a todos os módulos';
                  } else if (formData.nivel_acesso === 'visualizador') {
                    // Visualizador só pode ter mapa-integrado
                    if (modulo.key !== 'mapa-integrado') {
                      isDisabled = true;
                      disabledReason = 'Visualizadores só têm acesso ao Mapa Integrado';
                    }
                  } else if (formData.nivel_acesso === 'operador' && modulo.key === 'gestao-usuarios') {
                    // Operador não pode ter gestão de usuários
                    isDisabled = true;
                    disabledReason = 'Operadores não podem gerenciar usuários';
                  }
                  
                  return (
                    <div key={modulo.key} className={`permission-item ${isDisabled ? 'permission-disabled' : ''}`}>
                      <div className="permission-header">
                        <span className="permission-icon">{modulo.icon}</span>
                        <span className="permission-label">{modulo.label}</span>
                        {isDisabled && disabledReason && (
                          <span className="permission-disabled-reason" title={disabledReason}>
                            ⓘ
                          </span>
                        )}
                      </div>
                      <label className="permission-toggle">
                        <input
                          type="checkbox"
                          checked={formData.permissoes[modulo.key] || false}
                          onChange={(e) => handlePermissaoChange(modulo.key, e.target.checked)}
                          disabled={isDisabled}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  );
                })}
              </div>
              
              {formData.nivel_acesso === 'admin' && (
                <small style={{ color: '#666', fontSize: '12px', marginTop: '10px', display: 'block' }}>
                  Administradores têm acesso a todos os módulos automaticamente
                </small>
              )}
            </div>

            {/* Status */}
            {isEditing && (
              <div className="form-section">
                <h3>Status do Usuário</h3>
                
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="ativo"
                      checked={formData.ativo}
                      onChange={handleInputChange}
                    />
                    <span className="checkbox-custom"></span>
                    Usuário ativo no sistema
                  </label>
                  <small style={{ color: '#666', fontSize: '12px' }}>
                    Usuários inativos não conseguem fazer login no sistema
                  </small>
                </div>
              </div>
            )}
          </div>

          {/* Ações do formulário */}
          <div className="form-actions">
            <button
              type="button"
              className="button secondary"
              onClick={() => navigate('/gestao-usuarios')}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="button primary"
              disabled={loading}
            >
              <Save size={16} />
              {loading ? 'Salvando...' : (isEditing ? 'Atualizar Usuário' : 'Criar Usuário')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CadastroUsuario;